import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { PeriodSelector } from '../PeriodSelector';
import type { PeriodValue } from '../../lib/period';
import { BOOKING_STATUS_META, bookingFilterMatch } from '../../booking/mockData';
import type { BookingFilter, BookingNumbers, BookingRequestRow } from '../../booking/types';

export interface BookingRequestsViewProps {
  rows: BookingRequestRow[];
  numbers: BookingNumbers;
  period: PeriodValue;
  onPeriodChange: (v: PeriodValue) => void;
  onSettings?: () => void;
  onTriage?: (id: string) => void;
  onAppointment?: (id: string) => void;
}

const FILTERS: { key: BookingFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'booked', label: 'Booked' },
  { key: 'not_booked', label: 'Not booked' },
];

const TONE: Record<string, { bg: string; fg: string }> = {
  blue: { bg: 'var(--brand-indigo-tint)', fg: 'var(--brand-indigo)' },
  amber: { bg: '#FBF3E4', fg: '#8A6415' },
  green: { bg: '#E9F6EF', fg: 'var(--money-green)' },
  grey: { bg: '#F1F3F9', fg: 'var(--text-muted)' },
};

/**
 * Online bookings (spec 50 §3) — a read-only record of what the booking page is
 * doing. No row panel, no row buttons beyond the one link; no badge on the menu.
 */
export function BookingRequestsView({
  rows,
  numbers,
  period,
  onPeriodChange,
  onSettings,
  onTriage,
  onAppointment,
}: BookingRequestsViewProps) {
  const [filter, setFilter] = useState<BookingFilter>('all');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) => bookingFilterMatch(filter, r.status) && (needle === '' || r.patientName.toLowerCase().includes(needle)),
    );
  }, [rows, filter, q]);

  const counts = useMemo(() => {
    const c: Record<BookingFilter, number> = { all: 0, in_progress: 0, booked: 0, not_booked: 0 };
    for (const r of rows) for (const f of FILTERS) if (bookingFilterMatch(f.key, r.status)) c[f.key] += 1;
    return c;
  }, [rows]);

  return (
    <div className="mx-auto max-w-[1180px] px-[30px] py-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="m-0 text-[30px] font-bold leading-[1.1] tracking-[-0.03em] text-[var(--text-primary)]">
            Online bookings
          </h1>
          <p className="m-0 mt-2 text-[13px] text-[var(--text-muted)]">
            Requests that didn&rsquo;t end in a booking are in Triage Inbox.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <PeriodSelector variant="report" value={period} onChange={onPeriodChange} startedISO="2026-03-03" />
          <button
            type="button"
            onClick={onSettings}
            className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3.5 text-[13px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
          >
            <Icon kind="cog" size={14} stroke={1.8} /> Settings
          </button>
        </div>
      </header>

      {/* Four numbers */}
      <div className="mb-5 grid grid-cols-4 gap-4 max-[900px]:grid-cols-2">
        <Stat label="Requests" value={String(numbers.requests)} />
        <Stat label="Reached on the call" value={`${String(numbers.reachedPct)}%`} />
        <Stat label="Booked" value={String(numbers.booked)} />
        <Stat label="Showed up" value={`${String(numbers.showedPct)}%`} />
      </div>

      {/* Filters + search */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const on = f.key === filter;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => { setFilter(f.key); }}
                className={cn(
                  'rounded-full px-3 py-1.5 text-[12.5px] font-semibold',
                  on
                    ? 'bg-[var(--brand-indigo)] text-white'
                    : 'bg-[var(--surface-card)] text-[var(--text-secondary)] ring-1 ring-[var(--border-default)] hover:bg-[var(--surface-sunken)]',
                )}
              >
                {f.label}
                <span className={cn('ml-1.5 font-[var(--font-mono)] text-[11px]', on ? 'text-white/80' : 'text-[var(--text-muted)]')}>
                  {counts[f.key]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative">
          <Icon kind="search" size={14} stroke={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
            placeholder="Search by name or phone"
            className="h-9 w-[240px] rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] pl-9 pr-3 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-[14px] border border-[var(--border-default)] bg-[var(--surface-card)]" style={{ boxShadow: 'var(--sh-1)' }}>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--border-default)] text-left">
              {['Patient', 'Visit', 'Requested', 'Call', 'Insurance', 'Fee', 'Status'].map((h) => (
                <th key={h} className="px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">
                  No online requests yet.
                </td>
              </tr>
            ) : (
              filtered.map((r) => {
                const meta = BOOKING_STATUS_META[r.status];
                const t = TONE[meta.tone] ?? TONE.grey;
                return (
                  <tr key={r.id} className="border-b border-[var(--border-default)] last:border-0 align-top">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2 text-[13px] font-semibold text-[var(--text-primary)]">
                        {r.patientName}
                        <span
                          className="rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                          style={
                            r.patientKind === 'new'
                              ? { background: 'var(--brand-indigo-tint)', color: 'var(--brand-indigo)' }
                              : { background: '#F1F3F9', color: 'var(--text-muted)' }
                          }
                        >
                          {r.patientKind === 'new' ? 'New' : 'Returning'}
                        </span>
                      </div>
                      {r.bookedForLine ? <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">{r.bookedForLine}</div> : null}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="text-[13px] text-[var(--text-primary)]">{r.reason}</div>
                      <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">{r.visitWhen}</div>
                    </td>
                    <td className="px-4 py-3.5 text-[12.5px] text-[var(--text-secondary)]">{r.requested}</td>
                    <td className="px-4 py-3.5 text-[12.5px] text-[var(--text-secondary)]">{r.call}</td>
                    <td className="px-4 py-3.5 text-[12.5px] text-[var(--text-secondary)]">{r.insurance}</td>
                    <td className="px-4 py-3.5 text-[12.5px] font-semibold text-[var(--text-secondary)]">{r.fee}</td>
                    <td className="px-4 py-3.5">
                      <span className="inline-flex rounded-full px-2.5 py-1 text-[11.5px] font-semibold" style={{ background: t?.bg, color: t?.fg }}>
                        {meta.label}
                      </span>
                      {meta.link === 'triage' ? (
                        <button type="button" onClick={() => { onTriage?.(r.id); }} className="mt-1.5 block text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
                          In Triage &rarr;
                        </button>
                      ) : null}
                      {meta.link === 'appointment' ? (
                        <button type="button" onClick={() => { onAppointment?.(r.id); }} className="mt-1.5 block text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
                          Open the appointment
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[14px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-3.5" style={{ boxShadow: 'var(--sh-1)' }}>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">{label}</div>
      <div className="mt-1 font-[var(--font-mono)] text-[26px] font-bold leading-none tracking-[-0.02em] text-[var(--text-primary)]">
        {value}
      </div>
    </div>
  );
}
